import React, { FC, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Button, Popover, Spin, Table } from 'antd';
import type { PaginationProps } from 'antd/lib/pagination';
import { DownOutlined } from '@ant-design/icons';
import useTranslate from '@hooks/useTranslate';
import IUser, { IModerationHistoryRow } from '@interfaces/IUser';
import componentContent from '../UserInfo.content.json';
import css from '../UserInfo.module.less';

const EVENT_TYPE_LABEL: Record<string, string> = {
  PERMANENT_BAN: 'PERMANENT_BAN',

  TEMPORARY_BAN_1: 'TEMPORARY_BAN_1',
  TEMPORARY_BAN_3: 'TEMPORARY_BAN_3',
  TEMPORARY_BAN_7: 'TEMPORARY_BAN_7',
  TEMPORARY_BAN_14: 'TEMPORARY_BAN_14',
  TEMPORARY_BAN_30: 'TEMPORARY_BAN_30',
  TEMPORARY_BAN_90: 'TEMPORARY_BAN_90',

  UNBLOCK_MANUAL: 'UNBLOCK_MANUAL',
  UNBLOCK_AUTO: 'UNBLOCK_AUTO',

  AGE_BAN: 'AGE_BAN',
  AUTOBLOCK: 'AGE_BAN',
  REJECT: 'REJECT',
  LOGOUT: 'LOGOUT',
  IP_BAN: 'IP_BAN',
  CHATS_BLOCKED: 'CHATS_BLOCKED',
  SHADOW_BAN: 'SHADOW_BAN',
};

const getLocalizedValue = (content: Record<string, string>, value?: string | null) => {
  if (!value) return '—';

  const contentKey = EVENT_TYPE_LABEL[value] || value;
  return content[contentKey] || value;
};

const getResponsible = (
  row: IModerationHistoryRow,
  contentMe: string,
  authUser?: { id?: number | string; login?: string; nickname?: string },
) => {
  const src = row.eventSource ?? '';
  const srcEmail = row?.admin?.email ? ` ${row?.admin?.email}` : '';

  if (src.toUpperCase() === 'GETSTREAM') return 'getStream' + srcEmail;
  if (src.toUpperCase() === 'GAMERAM' || src.toUpperCase() === 'SYSTEM')
    return 'Gameram' + srcEmail;

  if (src.toUpperCase() === 'ADMIN') {
    if (
      authUser?.id != null &&
      row.adminId != null &&
      String(authUser.id) === String(row.adminId)
    ) {
      if (authUser.login) return `${authUser.login} (${contentMe})`;
      if (authUser.nickname) return `${authUser.nickname} (${contentMe})`;
      return 'Admin' + srcEmail;
    }
    return 'Admin' + srcEmail;
  }

  return src ? src + srcEmail : '—';
};

interface ModerationHistoryTableProps {
  itemId?: IUser['id'];
  moderationHistory: IModerationHistoryRow[];
  moderationHistoryRaw: IModerationHistoryRow;
  totalModerationHistory: number;
  paginationModerationHistory: PaginationProps;
  setPaginationModerationHistory: (pagination: PaginationProps) => void;
  getModerationHistoryLoading: boolean;
  authUser?: {
    id?: number | string;
    login?: string;
    nickname?: string;
  };
  onFetchModerationHistory: (params: {
    userId: IUser['id'];
    paginationModerationHistory: PaginationProps;
  }) => void;
}

const ModerationHistoryTable: FC<ModerationHistoryTableProps> = ({
  itemId,
  moderationHistory,
  moderationHistoryRaw,
  totalModerationHistory,
  paginationModerationHistory,
  setPaginationModerationHistory,
  getModerationHistoryLoading,
  authUser,
  onFetchModerationHistory,
}) => {
  const content = useTranslate(componentContent);

  const [visibleModerationHistory, setVisibleModerationHistory] = useState(false);
  const [isFirstLoadModerationHistory, setIsFirstLoadModerationHistory] = useState(true);

  useEffect(() => {
    if (moderationHistoryRaw !== null) {
      setIsFirstLoadModerationHistory(false);
    }
  }, [moderationHistoryRaw]);

  const moderationColumns = useMemo(
    () => [
      {
        title: content.moderationHistoryDateStart,
        dataIndex: 'startDate',
        key: 'startDate',
        width: 180,
      },
      {
        title: content.moderationHistoryDateEnd,
        dataIndex: 'endDate',
        key: 'endDate',
        width: 180,
      },
      {
        title: content.moderationHistoryEvent,
        dataIndex: 'event',
        key: 'event',
        width: 170,
      },
      {
        title: content.moderationHistoryReason,
        dataIndex: 'reason',
        key: 'reason',
        width: 160,
      },
      {
        title: content.moderationHistoryResponsible,
        dataIndex: 'responsible',
        key: 'responsible',
        width: 140,
      },
    ],
    [content],
  );

  const moderationRows = useMemo(() => {
    return moderationHistory.map((row, idx) => {
      const eventType = row.eventType ?? '';

      return {
        key: row.id ?? idx,
        startDate: row.banStartedAt ? moment(row.banStartedAt).format('DD.MM.YYYY HH:mm') : '—',
        endDate: row.banEndedAt ? moment(row.banEndedAt).format('DD.MM.YYYY HH:mm') : '—',
        event: getLocalizedValue(content, eventType),
        reason: getLocalizedValue(content, row.reason),
        responsible: getResponsible(row, content.moderationHistoryMe, authUser),
      };
    });
  }, [moderationHistory, authUser, content]);

  const moderationHistoryTable = (
    <div
      className={css.moderationPopoverContent}
      onClick={(e) => e.stopPropagation()}
    >
      <Table
        className={css.moderationTable}
        columns={moderationColumns}
        dataSource={moderationRows}
        loading={getModerationHistoryLoading}
        size='small'
        scroll={{ x: 830 }}
        pagination={{
          current: paginationModerationHistory.current ?? 1,
          pageSize: paginationModerationHistory.pageSize ?? 10,
          total: totalModerationHistory,
          onChange: (page, pageSize) => {
            const nextPagination: PaginationProps = {
              ...paginationModerationHistory,
              current: page,
              pageSize,
            };

            setPaginationModerationHistory(nextPagination);

            if (itemId) {
              onFetchModerationHistory({
                userId: itemId,
                paginationModerationHistory: nextPagination,
              });
            }
          },
          position: ['topRight'],
          size: 'small',
        }}
        locale={{ emptyText: content.moderationHistoruEmpty }}
      />
    </div>
  );

  if ((!moderationHistory || getModerationHistoryLoading) && isFirstLoadModerationHistory) {
    return (
      <Spin
        size='small'
        style={{ marginLeft: 8 }}
      />
    );
  }

  if (getModerationHistoryLoading) {
    return (
      <Spin
        size='small'
        style={{ marginLeft: 8 }}
      />
    );
  }

  if (moderationHistory.length === 0) {
    return (
      <>
        {content.moderationHistory}: ({content.moderationHistoryEmpty})
      </>
    );
  }

  if (!itemId) return null;

  return (
    <Popover
      placement='bottomLeft'
      trigger='click'
      visible={visibleModerationHistory}
      onVisibleChange={setVisibleModerationHistory}
      getPopupContainer={() => document.body}
      autoAdjustOverflow
      overlayClassName={css.moderationPopover}
      content={moderationHistoryTable}
    >
      <Button
        icon={<DownOutlined />}
        style={{
          marginLeft: 8,
          height: 20,
          width: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      />
    </Popover>
  );
};

export default ModerationHistoryTable;
